const FILE_TLDS = new Set([
  "png", "jpg", "jpeg", "gif", "svg", "webp", "avif", "ico", "bmp", "tiff",
  "css", "js", "mjs", "json", "map", "woff", "woff2", "ttf", "eot", "mp4", "webm", "pdf",
]);

const EMAIL_RE = /[A-Za-z0-9][A-Za-z0-9._%+'-]*@[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?)*\.[A-Za-z]{2,}/g;

/** "name [at] example [dot] com" -> "name@example.com" (only the bracketed forms, to avoid false matches). */
export function deobfuscateEmails(text: string): string {
  return text
    .replace(/\s*[\[(]\s*at\s*[\])]\s*/gi, "@")
    .replace(/\s*[\[(]\s*dot\s*[\])]\s*/gi, ".");
}

export function extractEmails(input: string, opts: { obfuscated?: boolean } = {}): string[] {
  const text = opts.obfuscated ? deobfuscateEmails(input) : input;
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of text.match(EMAIL_RE) ?? []) {
    const at = raw.lastIndexOf("@");
    const local = raw.slice(0, at).replace(/[.'-]+$/, "");
    const domain = raw.slice(at + 1);
    const tld = domain.slice(domain.lastIndexOf(".") + 1).toLowerCase();
    if (!local || FILE_TLDS.has(tld) || /^\d+x\./i.test(domain)) continue;
    // Domains are case-insensitive; keep the local part as written.
    const email = `${local}@${domain.toLowerCase()}`;
    const key = email.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(email);
  }
  return out;
}

const URL_RE = /(?<![\w@./-])(?:https?:\/\/|ftp:\/\/|www\.(?=[a-z0-9-]+\.[a-z]{2,}))[^\s<>"`]+/gi;

function balanced(url: string, open: string, close: string) {
  let o = 0, c = 0;
  for (const ch of url) { if (ch === open) o++; else if (ch === close) c++; }
  return [o, c];
}

export function cleanUrl(raw: string): string {
  let u = raw;
  for (;;) {
    const last = u[u.length - 1];
    if (!last) break;
    if (/[.,;:!?'"*]/.test(last)) { u = u.slice(0, -1); continue; }
    if (last === ")" || last === "]") {
      const open = last === ")" ? "(" : "[";
      const [o, c] = balanced(u, open, last);
      if (c > o) { u = u.slice(0, -1); continue; }
    }
    break;
  }
  return u;
}

export function extractUrls(text: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const m of text.match(URL_RE) ?? []) {
    const u = cleanUrl(m);
    if (u.length < 8 || seen.has(u)) continue;
    seen.add(u);
    out.push(u);
  }
  return out;
}

const TRACKING = /^(utm_[a-z]+|fbclid|gclid|msclkid|mc_cid|mc_eid|igshid|yclid|ref_src)$/i;
export function stripTracking(url: string): string {
  const q = url.indexOf("?");
  if (q < 0) return url;
  const hashAt = url.indexOf("#", q);
  const query = url.slice(q + 1, hashAt < 0 ? undefined : hashAt);
  const hash = hashAt < 0 ? "" : url.slice(hashAt);
  const kept = query.split("&").filter((kv) => kv && !TRACKING.test(kv.split("=")[0]));
  return url.slice(0, q) + (kept.length ? "?" + kept.join("&") : "") + hash;
}

export function domainOf(urlOrEmail: string): string {
  if (urlOrEmail.includes("@") && !/^[a-z]+:\/\//i.test(urlOrEmail)) return urlOrEmail.slice(urlOrEmail.lastIndexOf("@") + 1).toLowerCase();
  return urlOrEmail.replace(/^[a-z]+:\/\//i, "").replace(/[/?#].*$/, "").replace(/^www\./i, "").toLowerCase();
}

const csvCell = (v: string) => (/[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
export function toCsv(header: [string, string], items: string[]): string {
  return [header.join(","), ...items.map((i) => `${csvCell(i)},${csvCell(domainOf(i))}`)].join("\n");
}
